import { useState, useEffect } from 'react';
import { FileData } from 'src/components/textEditor/TextEditor';
import { useMedia } from 'src/store/media/useMedia';
import { DeleteMediaRequest, UploadMediaRequest } from 'src/types/helios/Media';
import { SubQuestion } from 'src/types/helios/Question';
import { replaceImagesWithMediaIds } from 'src/utils/mediaUtils';

interface UseMediaUploadOptions {
  onUploadComplete?: (updatedData: Record<string, string>) => void;
  onError?: (error: any) => void;
}

/**
 * Custom hook for handling media uploads with progress tracking
 */
export const useMediaUpload = (options?: UseMediaUploadOptions) => {
  const {
    uploadMultipleMedia,
    uploadMediaApiStatus,
    clearMediaAllApiStatus,
    deleteMedia,
    deleteMediaApiStatus,
  } = useMedia();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // Update progress when uploadMediaApiStatus.progress changes
  useEffect(() => {
    if (isUploading && uploadMediaApiStatus.progress) {
      const { completed, total } = uploadMediaApiStatus.progress;
      const percentage = Math.round((completed / total) * 100);
      setUploadProgress(percentage);

      // If all uploads are complete, set progress to 100%
      if (completed === total) {
        setUploadProgress(100);
      }
    }
  }, [uploadMediaApiStatus.progress, isUploading]);

  /**
   * Upload images from multiple content fields
   * @param contentFields Object with field names as keys and content as values
   * @param uploadRequestParams Parameters for upload request (programId, businessUnit, entityType, createdBy)
   * @returns Updated content fields with mediaIds instead of base64 images
   */
  const uploadImagesFromContent = async (
    allFiles: FileData[],
    uploadRequestParams: Omit<UploadMediaRequest, 'file' | 'contentType'>,
  ): Promise<Record<string, string> | undefined> => {
    if (allFiles.length === 0) {
      return undefined;
    }
    // Show upload progress
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Prepare upload requests for all images
      const uploadRequests = allFiles.map((image) => {
        const request = {
          ...uploadRequestParams,
          file: image.image,
          contentType: image.extension,
          fileName: image.fileName,
        };
        console.log('useMediaUpload - uploadfiles - Upload request:', {
          fileName: request.fileName,
          contentType: request.contentType,
        });
        return request;
      });

      // Upload all images
      const results = await uploadMultipleMedia(uploadRequests);

      // Process upload results
      const uploadResults = results.payload as Array<{ media: { mediaId: string } }>;
      const imageToMediaIdMap: Record<string, string> = {};
      if (uploadResults && uploadResults.length > 0) {
        // Create a map of base64 image to mediaId

        uploadResults.forEach((result, index) => {
          if (result && result.media && result.media.mediaId) {
            const image = allFiles[index];
            imageToMediaIdMap[image.image] = result.media.mediaId;
          }
        });
      }

      // Call onUploadComplete callback if provided
      options?.onUploadComplete?.(imageToMediaIdMap);

      return imageToMediaIdMap;
    } catch (error) {
      console.error('Error uploading multiple media:', error);
      options?.onError?.(error);
      return undefined;
    } finally {
      // Set a small delay before hiding the progress indicator to ensure the user sees 100%
      setTimeout(() => {
        setIsUploading(false);
      }, 500);
    }
  };

  const uploadfiles = async (
    allFiles: FileData[],
    uploadRequestParams: Omit<UploadMediaRequest, 'file' | 'contentType'>,
  ): Promise<string[]> => {
    const mediaIds: string[] = [];
    if (allFiles.length === 0) {
      return [];
    }

    // Show upload progress
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Prepare upload requests for all images
      const uploadRequests = allFiles.map((image) => ({
        ...uploadRequestParams,
        file: image.image,
        contentType: image.extension,
        fileName: image.fileName,
      }));

      // Upload all images
      const results = await uploadMultipleMedia(uploadRequests);

      // Process upload results
      const uploadResults = results.payload as Array<{ media: { mediaId: string } }>;

      if (uploadResults && uploadResults.length > 0) {
        uploadResults.forEach((result, index) => {
          if (result && result.media && result.media.mediaId) {
            mediaIds.push(result.media.mediaId);
          }
        });
      }

      return mediaIds;
    } catch (error) {
      console.error('Error uploading multiple media:', error);
      options?.onError?.(error);
      return mediaIds;
    } finally {
      // Set a small delay before hiding the progress indicator to ensure the user sees 100%
      setTimeout(() => {
        setIsUploading(false);
      }, 500);
    }
  };

  return {
    isUploading,
    uploadProgress,
    uploadImagesFromContent,
    uploadfiles,
    uploadMediaApiStatus,
    clearMediaAllApiStatus,
    deleteMedia,
    deleteMediaApiStatus,
  };
};
